'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useMemo, useState } from 'react';

import cx from 'classnames';
import Fuse from 'fuse.js';
import PropTypes from 'prop-types';

import CardNoResults from 'components/cards/CardNoResults';
import CardProduct from 'components/cards/CardProduct';
import FilterBody from 'components/ui/FilterBody/';
import ShowMore from 'components/ui/ShowMore';

import useQueryState from 'hooks/useQueryState';
import { blockRootProps } from 'utils';

import Cards, { computeItemVisibility } from './Cards';
import { computeFillerSpans } from './layout';

import styles from './BlockProducts.module.scss';

const BlockProducts = ({
    __typename,
    firstBlock,
    anchorId,
    backgroundColor,
    products: dirtyProducts,
    cards,
    cardDistribution,
    theme,
    headline,
    cta,
    useCategorySort,
    searchParams,
}) => {
    dirtyProducts ??= [];
    searchParams ??= {};
    theme ??= 'default';

    const [state, setState] = useQueryState(searchParams);

    const [search, setSearch] = useState('');

    const products = useMemo(() => {
        const ids = [];
        const dedupedProducts = dirtyProducts.filter(product => {
            if (!product?.sys?.id) {
                return false;
            }

            if (ids.includes(product.sys.id)) {
                console.warn(`Duplicate product "${product.displayName}"`);
                return false;
            }

            ids.push(product.sys.id);
            return true;
        });

        return useCategorySort
            ? dedupedProducts.sort((a, b) => {
                  return a.order - b.order;
              })
            : dedupedProducts;
    }, [dirtyProducts, useCategorySort]);

    const searcher = useMemo(() => {
        return new Fuse(products, {
            keys: ['displayName', 'pageTitle', 'searchKeywords'],
            shouldSort: true,
            threshold: 0.3,
        });
    }, [products]);

    // Run though all products to build a list of available filters
    const filters = useMemo(() => {
        return products.reduce((o, product) => {
            Object.entries(product.attributes || {}).forEach(([key, value]) => {
                const categoryAttribute =
                    product.category.attributes[key] || false;

                if (!categoryAttribute) {
                    return;
                }

                const options = categoryAttribute.options.filter(filter => {
                    if (categoryAttribute.type === 'multiple') {
                        return [...value].includes(`${filter?.id}`);
                    } else {
                        return `${filter.id}` === `${value}`;
                    }
                });

                if (options.length === 0) {
                    console.warn(
                        `Category attribute value of \`${value}\` not found attribute in ${key}: ${product.displayName}`
                    );
                    return;
                }

                const optionObj = options.reduce((obj, item) => {
                    obj[item.id] = item;
                    return obj;
                }, {});

                o[key] = {
                    ...categoryAttribute,
                    options: {
                        ...((o[key] && o[key].options) || {}),
                        ...optionObj,
                    },
                };
            });

            return o;
        }, {});
    }, [products]);

    const [filtered, setFiltered] = useState(state);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleSearch = useCallback(evt => {
        setSearch(evt.target.value);
        setIsExpanded(false);
    }, []);

    const handleClear = () => {
        setSearch('');
        setFiltered({});
        setState({});
        setIsExpanded(false);
    };

    const productsSearched =
        search.length === 0
            ? products
            : searcher.search(search).map(product => product.item);

    const productsFiltered = productsSearched.filter(product => {
        if (Object.keys(state).length === 0) {
            return true;
        }
        return Object.keys(state).every(attrKey => {
            if (!product?.attributes) {
                return false;
            }
            const option = product.category?.attributes[attrKey]?.options.find(
                option => option.slug == state[attrKey]
            );
            if (Array.isArray(product.attributes[attrKey])) {
                return product.attributes[attrKey].includes(`${option?.id}`);
            }
            return `${product.attributes[attrKey]}` === `${option?.id}`;
        });
    });

    const validProducts = useMemo(() => {
        return productsFiltered.filter(p => p?.category && p?.slug);
    }, [productsFiltered]);

    const handleChangeFilter = key => event => {
        const state = {
            ...filtered,
            [key]: event.currentTarget.value,
        };
        if (event.currentTarget.value === '') {
            delete state[key];
        }
        setState(state);
        setFiltered(state);
        setIsExpanded(false);
    };

    const isNoResults = validProducts.length === 0;
    const showCards =
        cards?.length > 0 &&
        search.length === 0 &&
        Object.keys(filtered).length === 0;

    const { cardVisibility, productVisibility, hasMore } = useMemo(() => {
        return computeItemVisibility(
            showCards ? cards : [],
            validProducts,
            cardDistribution
        );
    }, [showCards, cards, validProducts, cardDistribution]);

    const fillerSpans = useMemo(
        () =>
            computeFillerSpans(
                showCards ? cards : [],
                validProducts.length,
                cardDistribution
            ),
        [showCards, cards, validProducts.length, cardDistribution]
    );

    const hasAnyMore = hasMore.small || hasMore.large || hasMore.xxLarge;

    return (
        <div
            className={cx(styles.root, { [styles.firstBlock]: firstBlock })}
            {...blockRootProps(anchorId, __typename)}
        >
            <FilterBody
                onSearch={handleSearch}
                onClear={handleClear}
                showClear={
                    search.length > 0 || Object.keys(filtered).length > 0
                }
                theme={theme}
                headline={headline}
                cta={cta}
                backgroundColor={backgroundColor}
            >
                {Object.keys(filters).map(key => {
                    return Object.keys(filters[key].options).length >= 2 ? (
                        <FilterBody.Select
                            key={`${key}-select`}
                            filters={filters}
                            filtered={filtered}
                            keyValue={key}
                            label={filters[key].label}
                            value={filtered[key] || ''}
                            onChange={handleChangeFilter(key)}
                        />
                    ) : null;
                })}
            </FilterBody>
            <ul
                className={cx(styles.productList, {
                    [styles.isCollapsed]: !isExpanded,
                    [styles.hasMore]: hasAnyMore,
                })}
            >
                {showCards && (
                    <Cards
                        cards={cards}
                        cardDistribution={cardDistribution}
                        visibility={cardVisibility}
                    />
                )}
                {validProducts.map((product, i) => {
                    const itemVis = productVisibility[i] || {};
                    return (
                        <li
                            key={product.sys.id}
                            style={{ '--index': i }}
                            className={cx(styles.listItem, {
                                [styles.hideSmall]: itemVis.hideSmall,
                                [styles.hideLarge]: itemVis.hideLarge,
                                [styles.hideXXLarge]: itemVis.hideXXLarge,
                            })}
                        >
                            <CardProduct
                                __typename={product.__typename}
                                priority={!firstBlock ? false : i <= 3}
                                color={product.color}
                                category={product.category}
                                eyebrow={product.eyebrow}
                                image={
                                    theme === 'foodservice'
                                        ? product.cardImage
                                        : product.image
                                }
                                displayName={product.displayName}
                                slug={product.slug}
                                theme={product.theme}
                                icon={product.icon}
                                id={product.sys.id}
                            />
                        </li>
                    );
                })}
                <li
                    key="end-card"
                    className={cx(styles.listItem, {
                        [styles.filler]: !isNoResults,
                        [styles.fillerHideSmall]:
                            !isNoResults && !fillerSpans[2],
                        [styles.fillerHideLarge]:
                            !isNoResults && !fillerSpans[3],
                        [styles.fillerHideXXLarge]:
                            !isNoResults && !fillerSpans[4],
                    })}
                    style={
                        isNoResults
                            ? undefined
                            : {
                                  '--filler-span-2': fillerSpans[2] || 1,
                                  '--filler-span-3': fillerSpans[3] || 1,
                                  '--filler-span-4': fillerSpans[4] || 1,
                              }
                    }
                >
                    {isNoResults && <CardNoResults search={search} />}
                </li>
            </ul>
            {!isExpanded && hasAnyMore && (
                <ShowMore
                    className={cx(styles.showMore, {
                        [styles.showMoreSmall]: hasMore.small,
                        [styles.showMoreLarge]: hasMore.large,
                        [styles.showMoreXXLarge]: hasMore.xxLarge,
                    })}
                    onClick={() => setIsExpanded(true)}
                />
            )}
        </div>
    );
};

BlockProducts.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    firstBlock: PropTypes.bool,
    products: PropTypes.array,
    cards: PropTypes.array,
    cardDistribution: PropTypes.oneOf(['Sparse', 'Dense']),
    backgroundColor: PropTypes.string,
    theme: PropTypes.string,
    headline: PropTypes.string,
    cta: PropTypes.object,
    useCategorySort: PropTypes.bool,
    searchParams: PropTypes.object,
};

export default BlockProducts;
